import { theme } from "@product/scmp-sdk";
import { useDebounceEffect } from "ahooks";
import type { FunctionComponent } from "react";
import { Suspense, useCallback, useEffect, useState } from "react";
import { graphql, useFragment, useQueryLoader } from "react-relay";

import { useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import { ImageSize } from "~/components/article/article-leading/article-leading-images/enums";
import type { ArticleRenderChildProps } from "~/components/article/article-render";
import { useReduceWhitespaceByAvailableSpace } from "~/components/article/article-render/hooks";
import { LiveArticleNewsletterWidget } from "~/components/article/article-render/live-article/newsletter-widget";
import type { liveArticleArticle$key } from "~/queries/__generated__/liveArticleArticle.graphql";
import type { liveArticleQuery } from "~/queries/__generated__/liveArticleQuery.graphql";

import { useLiveUpdate } from "./hooks";
import {
  Author,
  Comment,
  Content,
  DesktopHeaderAdSlot,
  Follow,
  Headline,
  Introduction,
  Leading,
  Left,
  LiveArticleContainer,
  LiveSubscribe,
  LiveUpdateAlert,
  LiveUpdateAlertContainer,
  MobileContentFooterAdSlot,
  MobileContentHeaderAdSlot,
  MobileFooterAdSlot,
  MobileFooterNewsletter,
  MoreOnThis,
  NewsletterWidgetContainer,
  RightAvailableSpaceTracker,
  Section,
  SharePanel,
  Sponsor,
  StyledArticleContentRightSection,
  StyledLiveArticleContent,
  StyledLiveArticleLiveUpdateSwitch,
  StyledLiveArticleTimeline,
  SubHeadline,
  TimelinePad,
  TimelineWrapper,
  Topic,
} from "./styles";

type Props = {
  reference: liveArticleArticle$key;
} & ArticleRenderChildProps;

export const query = graphql`
  query liveArticleQuery($entityUuid: String, $randomString: String) {
    liveArticle(filter: { entityUuid: $entityUuid, randomString: $randomString }) {
      ...liveUpdateSwitchLiveArticleLiveArticle
      ...hooksLiveArticleLiveArticle
      ...contentLiveArticleLiveArticle
    }
  }
`;

export const LiveArticle: FunctionComponent<Props> = ({ provider, reference }) => {
  const [queryReference, loadQuery, disposeQuery] = useQueryLoader<liveArticleQuery>(query);
  const [isDynamicContentLoaded, setIsDynamicContentLoaded] = useState(false);

  useDebounceEffect(
    () => {
      if (!queryReference) return;
      setIsDynamicContentLoaded(true);
    },
    [queryReference],
    { wait: 2000 },
  );

  const { appBarStickyHeight } = useAppBarStickyHeightValue();

  const article = useFragment(
    graphql`
      fragment liveArticleArticle on Article {
        entityUuid
        entityId
        ...contentLiveArticleArticle
        ...liveUpdateSwitchLiveArticleArticle
        ...newsletterWidgetLiveArticleArticle
        ...reduceWhitespaceByAvailableSpaceArticle
      }
    `,
    reference,
  );

  const { reduceWhitespaceByAvailableSpaceSpec } = useReduceWhitespaceByAvailableSpace({
    isEnabled: isDynamicContentLoaded,
    isShowRightMoreOnThis: false,
    reference: article,
    specs: {
      adSlotSize: 600,
      nativeAdSlotSize: 260,
      whitespaceAllowance: 100,
    },
    trackers: {
      right: RightAvailableSpaceTracker,
    },
  });

  const articleEntityUuid = article.entityUuid;

  useEffect(() => {
    if (!articleEntityUuid) return;
    loadQuery({
      entityUuid: articleEntityUuid,
      randomString: "",
    });
    return disposeQuery;
  }, [disposeQuery, loadQuery, articleEntityUuid]);

  const refreshQuery = useCallback(() => {
    loadQuery(
      {
        entityUuid: articleEntityUuid,
        randomString: "",
      },
      { fetchPolicy: "store-and-network" },
    );
  }, [articleEntityUuid, loadQuery]);

  const { newUpdateCount, reset } = useLiveUpdate(articleEntityUuid, () => {
    refreshQuery();
  });

  return (
    <LiveArticleContainer>
      {newUpdateCount >= 1 && (
        <LiveUpdateAlertContainer $stickyStackedHeight={appBarStickyHeight}>
          <LiveUpdateAlert
            onClick={() => {
              refreshQuery();
              reset();
            }}
          >
            {newUpdateCount} New Update{newUpdateCount > 1 ? "s" : ""}
          </LiveUpdateAlert>
        </LiveUpdateAlertContainer>
      )}

      <Leading>
        {provider.leading({
          responsiveDescriptionVariants: {
            desktopUp: "show",
            mobileUp: "hidden",
            tabletUp: "show",
          },
          responsiveImageVariants: { desktopUp: "normal", mobileUp: "normal", tabletUp: "normal" },
          responsiveVariants: {
            desktopUp: ImageSize["1200x800"],
            mobileUp: ImageSize["1200x800"],
            tabletUp: ImageSize["1200x800"],
          },
        })}
      </Leading>
      <Section>{provider.sections()}</Section>
      <Headline>{provider.headline()}</Headline>
      <SubHeadline>{provider.subHeadline()}</SubHeadline>
      <Sponsor>{provider.sponsor()}</Sponsor>
      <Follow>{provider.topicFollowWidget({ variant: "banner" })}</Follow>
      <Author>{provider.authorBlock()}</Author>

      <Introduction>Introduction</Introduction>

      <Content>
        {provider.body({
          extraComponentMap: {
            "inline-widget": null,
          },
        })}
      </Content>

      <Suspense fallback={<></>}>
        {queryReference && (
          <>
            <LiveSubscribe>
              <StyledLiveArticleLiveUpdateSwitch
                queryReference={queryReference}
                reference={article}
              />
            </LiveSubscribe>
            <StyledLiveArticleContent
              provider={provider}
              queryReference={queryReference}
              reference={article}
            />
          </>
        )}
      </Suspense>

      <Comment>{provider.commentTrigger()}</Comment>
      <MoreOnThis>{provider.moreOnThis({ variant: "horizontal" })}</MoreOnThis>
      <Topic>{provider.topics()}</Topic>

      <Left $stickyStackedHeight={appBarStickyHeight}>
        <SharePanel>
          {provider.shareWidget()}
          {provider.actionWidget()}
        </SharePanel>

        <TimelineWrapper>
          <Suspense fallback={<></>}>
            {queryReference && <StyledLiveArticleTimeline queryReference={queryReference} />}
          </Suspense>
          <TimelinePad />
        </TimelineWrapper>
      </Left>

      <StyledArticleContentRightSection
        {...reduceWhitespaceByAvailableSpaceSpec.right}
        entityId={article.entityId}
        provider={provider}
        rightSectionCustomSlot={
          <NewsletterWidgetContainer>
            <LiveArticleNewsletterWidget reference={article} />
          </NewsletterWidgetContainer>
        }
      />
      {reduceWhitespaceByAvailableSpaceSpec.right.trackerNode}

      <DesktopHeaderAdSlot>
        {provider.adslot({
          adUnit: "d_banner1",
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
        })}
      </DesktopHeaderAdSlot>

      <MobileContentHeaderAdSlot>
        {provider.adslot({
          adUnit: "m_banner1",
          breakpoint: theme.breakpoints.down("tablet"),
          sizes: [[300, 250], "fluid"],
          withBackground: true,
          withLabel: true,
        })}
      </MobileContentHeaderAdSlot>

      <MobileContentFooterAdSlot>
        {provider.adslot({
          adUnit: "native4",
          breakpoint: theme.breakpoints.down("tablet"),
          sizes: ["fluid"],
        })}
      </MobileContentFooterAdSlot>

      <MobileFooterNewsletter>
        <LiveArticleNewsletterWidget reference={article} />
      </MobileFooterNewsletter>

      <MobileFooterAdSlot>
        {provider.adslot({
          adUnit: "m_lrec3a",
          breakpoint: theme.breakpoints.down("tablet"),
          sizes: [[300, 250], "fluid"],
          withBackground: false,
          withLabel: true,
        })}
      </MobileFooterAdSlot>
    </LiveArticleContainer>
  );
};

LiveArticle.displayName = "LiveArticle";
